import {Box, BoxProps} from "@cdx/ds";
import {DragCb, useSimpleDrag} from "../../lib/hooks/use-drag";
import {MutableRefObject, ReactNode} from "react";
import {NodeSide, VisionBoardNode} from "../../cdx-models/utils/extended-fields";
import {adaptToGrid, BushRect, TwoNums, VisionBoardBag} from "./canvas-utils";
import uiClasses from "@cdx/common/xui/ui.css";
import {onEdgeDrop, useCanvasTempEdgeStore} from "./CanvasEdge";
import {NodeBushEntry} from "./useVisionBoardData";

type Pos = readonly [x: number, y: number];

const Handle = ({onDrag, dot, ...rest}: {onDrag: DragCb; dot?: ReactNode} & BoxProps) => {
  const handlers = useSimpleDrag(onDrag);
  return (
    <Box absolute pointerEvents="auto" {...rest} {...handlers}>
      {dot}
    </Box>
  );
};

const BorderDot = ({
  side,
  bushNode,
  bag,
  ...rest
}: {side: NodeSide; bushNode: NodeBushEntry; bag: VisionBoardBag} & BoxProps) => {
  const handlers = useSimpleDrag((action, xy) => {
    switch (action) {
      case "start":
        useCanvasTempEdgeStore.setState({
          arrow: {type: "creation", source: {node: bushNode, side}, relativeTarget: xy},
        });
        break;
      case "move":
        useCanvasTempEdgeStore.setState(() => ({
          arrow: {type: "creation", source: {node: bushNode, side}, relativeTarget: xy},
        }));
        break;
      case "end":
        const edgeParams = onEdgeDrop(
          {type: "creation", source: {node: bushNode, side}, relativeTarget: xy},
          bag
        );
        bag.store.addEdge(edgeParams);
        useCanvasTempEdgeStore.setState({arrow: null});
        break;
      case "cancel":
        useCanvasTempEdgeStore.setState({arrow: null});
        break;
    }
  });
  return (
    <Box
      absolute
      rounded="full"
      colorTheme="active200"
      bg="foreground"
      cursor="pointer"
      className={uiClasses.hideElement}
      {...rest}
      {...handlers}
      style={{
        width: 10,
        height: 10,
      }}
    />
  );
};

// const MIN_WIDTH = 150;
// const MIN_HEIGHT = 50;

const clampPos = (
  [[dx, dy], [dw, dh]]: [Pos, Pos],
  rect: BushRect,
  scale: number,
  [minW, minH]: TwoNums,
  forceAspect?: number
) => {
  const w = rect.maxX - rect.minX;
  const h = rect.maxY - rect.minY;

  const nextX = rect.minX + dx / scale;
  const nextY = rect.minY + dy / scale;
  const nextW = w + dw / scale;
  const nextH = h + dh / scale;
  const minHeight = forceAspect ? minW / forceAspect : minH;
  return [
    [Math.min(nextX, rect.maxX - minW), Math.min(nextY, rect.maxY - minHeight)],
    [Math.max(nextW, minW), Math.max(nextH, minHeight)],
  ];
};

type BaseResizeHandlersProps = {
  forceAspect?: number;
  borderDotCtx?: {bushNode: NodeBushEntry; bag: VisionBoardBag};
  setPos: (rect: BushRect, mode: "move" | "end" | "cancel") => void;
  orgPos: BushRect;
  bag: VisionBoardBag;
  minDims: TwoNums;
};

export const BaseResizeHandlers = ({
  forceAspect,
  borderDotCtx,
  orgPos,
  setPos,
  bag,
  minDims,
}: BaseResizeHandlersProps) => {
  const orgH = orgPos.maxY - orgPos.minY;
  const orgW = orgPos.maxX - orgPos.minX;

  const getDragHandler: (posToMov: (p: Pos) => [Pos, Pos]) => DragCb = (posToMov) => {
    return (action, movement) => {
      const [[x, y], [w, h]] = clampPos(
        posToMov(movement),
        orgPos,
        bag.posRef.current.scale,
        minDims,
        forceAspect
      );
      switch (action) {
        case "move":
        case "end":
          return setPos({minX: x, minY: y, maxX: x + w, maxY: y + h}, action);
        case "cancel":
          return setPos(orgPos, "cancel");
      }
    };
  };

  return (
    <>
      {/* North */}
      <Handle
        onDrag={getDragHandler(([, y]) => [
          [0, y],
          [-(forceAspect ? (orgH + y) * forceAspect - orgW : 0), -y],
        ])}
        cursor="row-resize"
        left="0"
        width="100%"
        height="8px"
        top="0"
        translateY="-50%"
        className={uiClasses.hideContainer}
        dot={
          borderDotCtx && (
            <BorderDot
              left="50%"
              bottom="50%"
              translateX="-50%"
              translateY="50%"
              side="top"
              {...borderDotCtx}
            />
          )
        }
      />
      {/* South */}
      <Handle
        onDrag={getDragHandler(([, y]) => [
          [0, 0],
          forceAspect ? [(orgH + y) * forceAspect - orgW, y] : [0, y],
        ])}
        cursor="row-resize"
        bottom="0"
        width="100%"
        height="8px"
        translateY="50%"
        className={uiClasses.hideContainer}
        dot={
          borderDotCtx && (
            <BorderDot
              left="50%"
              bottom="50%"
              translateX="-50%"
              translateY="50%"
              side="bottom"
              {...borderDotCtx}
            />
          )
        }
      />
      {/* West */}
      <Handle
        onDrag={getDragHandler(([x]) => [
          [x, 0],
          [-x, -(forceAspect ? (orgW + x) / forceAspect - orgH : 0)],
        ])}
        cursor="col-resize"
        top="0"
        height="100%"
        width="8px"
        left="0"
        translateX="-50%"
        className={uiClasses.hideContainer}
        dot={
          borderDotCtx && (
            <BorderDot
              top="50%"
              right="50%"
              translateX="50%"
              translateY="-50%"
              side="left"
              {...borderDotCtx}
            />
          )
        }
      />
      {/* East */}
      <Handle
        onDrag={getDragHandler(([x]) => [
          [0, 0],
          forceAspect ? [x, (orgW + x) / forceAspect - orgH] : [x, 0],
        ])}
        cursor="col-resize"
        top="0"
        right="0"
        height="100%"
        width="8px"
        translateX="50%"
        className={uiClasses.hideContainer}
        dot={
          borderDotCtx && (
            <BorderDot
              top="50%"
              right="50%"
              translateX="50%"
              translateY="-50%"
              side="right"
              {...borderDotCtx}
            />
          )
        }
      />
      {/* Nort West */}
      <Handle
        onDrag={getDragHandler(([x, y]) => {
          if (forceAspect) {
            const altY = (orgW + x) / forceAspect - orgH;
            const altX = (orgH + y) * forceAspect - orgW;
            return [
              [Math.min(altX, x), Math.min(altY, y)],
              [-Math.min(altX, x), -Math.min(altY, y)],
            ];
          } else {
            return [
              [x, y],
              [-x, -y],
            ];
          }
        })}
        cursor="nwse-resize"
        top="0"
        left="0"
        height="12px"
        width="12px"
        translateX="-50%"
        translateY="-50%"
      />
      {/* Nort East */}
      <Handle
        onDrag={getDragHandler(([x, y]) => {
          if (forceAspect) {
            const altY = (orgW + x) / forceAspect - orgH;
            const altX = (orgH - y) * forceAspect - orgW;
            return [
              [0, Math.min(-altY, y)],
              [Math.max(altX, x), -Math.min(-altY, y)],
            ];
          } else {
            return [
              [0, y],
              [x, -y],
            ];
          }
        })}
        cursor="nesw-resize"
        top="0"
        right="0"
        height="12px"
        width="12px"
        translateX="50%"
        translateY="-50%"
      />
      {/* South West */}
      <Handle
        onDrag={getDragHandler(([x, y]) => {
          if (forceAspect) {
            const altX = orgW - (orgH + y) * forceAspect;
            const altY = (orgW - x) / forceAspect - orgH;
            return [
              [Math.min(altX, x), 0],
              [-Math.min(altX, x), Math.max(altY, y)],
            ];
          } else {
            return [
              [x, 0],
              [-x, y],
            ];
          }
        })}
        cursor="nesw-resize"
        bottom="0"
        left="0"
        height="12px"
        width="12px"
        translateX="-50%"
        translateY="50%"
      />
      {/* South East */}
      <Handle
        onDrag={getDragHandler(([x, y]) => {
          if (forceAspect) {
            const altY = (orgW + x) / forceAspect - orgH;
            const altX = (orgH + y) * forceAspect - orgW;
            return [
              [0, 0],
              [Math.max(altX, x), Math.max(altY, y)],
            ];
          } else {
            return [
              [0, 0],
              [x, y],
            ];
          }
        })}
        cursor="nwse-resize"
        bottom="0"
        right="0"
        height="12px"
        width="12px"
        translateX="50%"
        translateY="50%"
      />
    </>
  );
};

const OnlyDots = ({bag, bushNode}: {bag: VisionBoardBag; bushNode: NodeBushEntry}) => (
  <>
    {/* North */}
    <Box
      absolute
      left="0"
      width="100%"
      height="8px"
      top="0"
      translateY="-50%"
      className={uiClasses.hideContainer}
    >
      <BorderDot
        left="50%"
        bottom="50%"
        translateX="-50%"
        translateY="50%"
        side="top"
        bag={bag}
        bushNode={bushNode}
      />
    </Box>
    {/* South */}
    <Box
      absolute
      bottom="0"
      width="100%"
      height="8px"
      translateY="50%"
      className={uiClasses.hideContainer}
    >
      <BorderDot
        left="50%"
        bottom="50%"
        translateX="-50%"
        translateY="50%"
        side="bottom"
        bag={bag}
        bushNode={bushNode}
      />
    </Box>
    {/* West */}
    <Box
      absolute
      top="0"
      height="100%"
      width="8px"
      left="0"
      translateX="-50%"
      className={uiClasses.hideContainer}
    >
      <BorderDot
        top="50%"
        right="50%"
        translateX="50%"
        translateY="-50%"
        side="left"
        bag={bag}
        bushNode={bushNode}
      />
    </Box>
    {/* East */}
    <Box
      absolute
      top="0"
      right="0"
      height="100%"
      width="8px"
      translateX="50%"
      className={uiClasses.hideContainer}
    >
      <BorderDot
        top="50%"
        right="50%"
        translateX="50%"
        translateY="-50%"
        side="right"
        bag={bag}
        bushNode={bushNode}
      />
    </Box>
  </>
);

const ResizeHandles = ({
  containerRef,
  orgPos,
  bushNode,
  bag,
  forceAspect,
  disableResize,
  minDims,
}: {
  containerRef: MutableRefObject<HTMLDivElement | null>;
  orgPos: VisionBoardNode["pos"];
  bushNode: NodeBushEntry;
  bag: VisionBoardBag;
  forceAspect?: number;
  disableResize?: boolean;
  minDims: TwoNums;
}) => {
  if (disableResize) {
    return <OnlyDots bag={bag} bushNode={bushNode} />;
  } else {
    const setPos = (rect: BushRect, mode: "move" | "end" | "cancel") => {
      const el = containerRef.current!;
      const [sx, sy] = adaptToGrid([rect.minX, rect.minY], bag.store);
      const [sw, sh] = adaptToGrid([rect.maxX - sx, rect.maxY - sy], bag.store);
      el.style.left = `${sx}px`;
      el.style.top = `${sy}px`;
      el.style.width = `${sw}px`;
      el.style.height = `${sh}px`;
      bag.store.emitNodeMoveListener(bushNode.id, {
        type: "rect",
        value: {
          minX: sx,
          minY: sy,
          maxX: sx + sw,
          maxY: sy + sh,
        },
      });
      if (mode === "end") {
        bushNode.node.set("pos", {...orgPos, x: sx, y: sy, w: sw, h: sh});
      }
    };
    return (
      <BaseResizeHandlers
        forceAspect={forceAspect}
        borderDotCtx={{bushNode, bag}}
        setPos={setPos}
        orgPos={bushNode}
        bag={bag}
        minDims={minDims}
      />
    );
  }
};

export default ResizeHandles;
